import { useEffect, useMemo, useState } from "react";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { typeIcon } from "./forms-tab-step-outputs";
import "./forms-tab-add-field-modal.scss";

/** A workflow parameter that can be pulled into the form as a field. */
export interface IAddFieldParamOption {
  id: string;
  name: string;
  /** Data type shown in parentheses next to the name (e.g. "String", "DateTime Offset"). */
  typeName: string;
  /** True for Business Object parameters; false for internal parameters. Drives grouping. */
  isBusinessObject: boolean;
}

export interface IFormsTabAddFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Parameters not yet present in the form, offered for selection. */
  options: IAddFieldParamOption[];
  onSave: (selectedIds: string[]) => void;
}

/**
 * "Add Field" picker: choose Business Object and/or internal parameters to add to the form.
 * Multi-select with search; committing appends the chosen parameters as fields in the shell.
 */
const FormsTabAddFieldModal = ({ isOpen, onClose, options, onSave }: IFormsTabAddFieldModalProps) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState<string>("");

  // Reset selection every time the picker opens so a prior aborted session doesn't leak in.
  useEffect((): void => {
    if (isOpen) {
      setSelected(new Set());
      setSearch("");
    }
  }, [isOpen]);

  const filtered: IAddFieldParamOption[] = useMemo((): IAddFieldParamOption[] => {
    const needle: string = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option: IAddFieldParamOption): boolean => option.name.toLowerCase().includes(needle));
  }, [options, search]);

  const businessObjects: IAddFieldParamOption[] = filtered.filter(
    (option: IAddFieldParamOption): boolean => option.isBusinessObject
  );
  const internals: IAddFieldParamOption[] = filtered.filter(
    (option: IAddFieldParamOption): boolean => !option.isBusinessObject
  );

  const toggle = (id: string): void => {
    setSelected((prev: Set<string>): Set<string> => {
      const next: Set<string> = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderGroup = (title: string, group: IAddFieldParamOption[]) => {
    if (group.length === 0) return null;
    return (
      <div className="kuika-forms-add-field-modal__group">
        <span className="kuika-forms-add-field-modal__group-title">{title}</span>
        {group.map((option: IAddFieldParamOption) => {
          const isChecked: boolean = selected.has(option.id);
          return (
            <div
              key={option.id}
              className={`kuika-forms-add-field-modal__row${isChecked ? " kuika-forms-add-field-modal__row--checked" : ""}`}
              onClick={(): void => toggle(option.id)}
              data-testid={`processFormsAddFieldOption-${option.id}`}
            >
              {/* Stop the checkbox's own click bubbling so it doesn't double-toggle with the row. */}
              <span
                className="kuika-forms-add-field-modal__check"
                onClick={(e: React.MouseEvent<HTMLSpanElement>): void => e.stopPropagation()}
              >
                <KuikaCheckbox checked={isChecked} onChange={(): void => toggle(option.id)} />
              </span>
              <KuikaSvg
                className="kuika-forms-add-field-modal__icon"
                name={typeIcon(option.typeName)}
                width={16}
                height={16}
                color="var(--dark-9)"
              />
              <span className="kuika-forms-add-field-modal__name">{option.name}</span>
              <span className="kuika-forms-add-field-modal__type">({option.typeName || "—"})</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <KModal
      showModal={isOpen}
      closeModal={onClose}
      width={460}
      header="Add Field"
      footer={
        <KModalFooter
          onCancel={onClose}
          cancelTestId="processFormsAddFieldCancel"
          submitLabel="Save"
          submitTestId="processFormsAddFieldSave"
          disabled={selected.size === 0}
          onSubmit={(): void => onSave([...selected])}
        />
      }
    >
      <div className="kuika-forms-add-field-modal">
        <div className="kuika-forms-add-field-modal__count">
          {selected.size} of {options.length} selected
        </div>
        <KuikaInput
          className="kuika-forms-add-field-modal__search"
          size="md"
          placeholder="Search parameters"
          value={search}
          leftIcon={<KuikaSvg name="search" width={14} height={14} />}
          onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void => setSearch(String(value))}
          data-testid="processFormsAddFieldSearch"
        />
        <div className="kuika-forms-add-field-modal__body">
          {renderGroup("BUSINESS OBJECTS", businessObjects)}
          {renderGroup("INTERNAL PARAMETERS", internals)}
          {filtered.length === 0 && (
            <div className="kuika-forms-add-field-modal__empty">All parameters are already on the form.</div>
          )}
        </div>
      </div>
    </KModal>
  );
};

export default FormsTabAddFieldModal;
